import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState, type ReactElement } from 'react';

import { Capability } from '@mpr/shared';

import { api } from './lib/api.js';
import type { StudioHandoff } from './lib/studioProject.js';
import { useI18n } from './i18n/LanguageProvider.js';
import type { TranslationKey } from './i18n/translations.js';
import { Icon } from './components/Icons.js';
import { useTheme } from './components/ThemeProvider.js';
import { AccountMenu } from './components/AccountMenu.js';
import { LoginPanel } from './components/LoginPanel.js';
import { ConnectionsPanel } from './components/ConnectionsPanel.js';
import { SocialConnectionsPanel } from './components/SocialConnectionsPanel.js';
import { InboxPanel } from './components/InboxPanel.js';
import { MentionsPanel } from './components/MentionsPanel.js';
import { PageProfilePanel } from './components/PageProfilePanel.js';
import { PagesPanel } from './components/PagesPanel.js';
import { CommentsPanel } from './components/CommentsPanel.js';
import { AutomationPanel } from './components/AutomationPanel.js';
import { PublishedPostsPanel } from './components/PublishedPostsPanel.js';
import { JobsPanel } from './components/JobsPanel.js';
import { LogsPanel } from './components/LogsPanel.js';
import { HomePanel } from './components/HomePanel.js';
import { InsightsPanel } from './components/InsightsPanel.js';
import { PublishFlow } from './components/PublishFlow.js';
import { useTableLabels } from './lib/tableLabels.js';
import { AccountPanel } from './components/AccountPanel.js';
import { AdminPanel } from './components/AdminPanel.js';
import { WhatsappPanel } from './components/WhatsappPanel.js';
import { ConversationsPanel } from './components/ConversationsPanel.js';
import { UtilityTemplatesPanel } from './components/UtilityTemplatesPanel.js';
import { StudioPanel } from './components/StudioPanel.js';

/**
 * Every tab, and the capability that opens it.
 *
 * `needs` names the capability the panel's own work requires — the one its main
 * request is already guarded by on the server. It is the shared constant and
 * not a string: these used to be spelled `'PAGES_READ'` here while the server
 * sent `'pages.read'`, so nothing ever matched and every tab with a
 * requirement disappeared — an administrator saw two screens of sixteen. A
 * misspelling is now a compile error rather than a silently empty dashboard. A person without it saw the tab,
 * pressed it, and got a panel of errors; the dashboard offered a room they
 * could not enter. Now they are not offered it.
 *
 * This hides, it does not enforce. Every route checks for itself, and a tab put
 * back by hand in the developer console still buys nothing. What it buys is a
 * dashboard that fits the job: an operator opens the application and sees the
 * inbox, not eleven tabs that answer 403.
 *
 * `home` has no `needs` on purpose — it is where everybody lands, and it shows
 * only what its own requests are allowed to return.
 */
const TABS = [
  { id: 'home', label: 'nav.home', icon: Icon.home },
  {
    id: 'connections',
    label: 'tab.connections',
    icon: Icon.link,
    needs: Capability.CONNECTIONS_READ,
  },
  { id: 'pages', label: 'tab.pages', icon: Icon.pages, needs: Capability.PAGES_READ },
  { id: 'page-profile', label: 'profile.title', icon: Icon.user, needs: Capability.PAGES_WRITE },
  {
    id: 'whatsapp',
    label: 'whatsapp.title',
    icon: Icon.link,
    needs: Capability.SETTINGS_READ,
    channel: 'WHATSAPP',
  },
  { id: 'publish', label: 'tab.publish', icon: Icon.send, needs: Capability.POSTS_WRITE },
  { id: 'studio', label: 'tab.studio', icon: Icon.sparkle, needs: Capability.POSTS_WRITE },
  { id: 'jobs', label: 'tab.jobs', icon: Icon.list, needs: Capability.POSTS_READ },
  {
    id: 'conversations',
    label: 'tab.conversations',
    icon: Icon.comment,
    needs: Capability.INBOX_READ,
  },
  { id: 'inbox', label: 'inbox.title', icon: Icon.comment, needs: Capability.INBOX_READ },
  { id: 'comments', label: 'tab.comments', icon: Icon.comment, needs: Capability.INBOX_READ },
  { id: 'mentions', label: 'mentions.title', icon: Icon.comment, needs: Capability.INBOX_READ },
  { id: 'utility', label: 'utility.title', icon: Icon.clock, needs: Capability.INBOX_READ },
  { id: 'automation', label: 'tab.automation', icon: Icon.robot, needs: Capability.SETTINGS_READ },
  { id: 'published', label: 'tab.published', icon: Icon.check, needs: Capability.POSTS_READ },
  { id: 'insights', label: 'insights.title', icon: Icon.chart, needs: Capability.INSIGHTS_READ },
  { id: 'logs', label: 'tab.logs', icon: Icon.logs, needs: Capability.SETTINGS_READ },
  { id: 'account', label: 'tab.account', icon: Icon.user, needs: Capability.ACCOUNT_READ },
  { id: 'admin', label: 'tab.admin', icon: Icon.shield, superAdminOnly: true },
] as const satisfies ReadonlyArray<{
  id: string;
  label: TranslationKey;
  icon: unknown;
  needs?: Capability;
  /**
   * A channel this installation may not offer. Absent means the tab stands on
   * its own — most of the dashboard is Pages, which is the whole product.
   */
  channel?: string;
  superAdminOnly?: boolean;
}>;

type TabId = (typeof TABS)[number]['id'];

/**
 * Sections group the panels a person moves through while doing one thing.
 *
 * Publishing used to be four destinations — pick a source, publish it, see what
 * published, watch the job — for what is a single task, and the chosen post had
 * to be carried across all four in the operator's head. Connecting an account
 * and listing its Pages were likewise split, though nobody does one without the
 * other.
 *
 * Grouping leaves five destinations instead of eleven. That is the number that
 * fits in a thumb-reachable bar on a phone without scrolling, which is where
 * most of this product is used.
 */
/**
 * The five rooms of the product, and one for the wiring.
 *
 * The rule that decides where a panel goes: **is it the work, or is it what the
 * work runs on?** Setting up an account is done once and then almost never;
 * answering customers is done all day. They were mixed together — connecting a
 * Meta account sat beside the list of Pages, and binding an assistant sat in
 * the middle of the inbox — so configuration kept surfacing in the rooms people
 * live in, and somebody looking for a setting had to remember which room it had
 * been filed under.
 *
 * Everything that configures now sits in one place. The work sections got
 * shorter for it, which was the point.
 */
const SECTIONS = [
  { id: 'home', label: 'nav.home', icon: Icon.home, tabs: ['home'] },
  {
    id: 'pages',
    label: 'nav.pages',
    icon: Icon.pages,
    tabs: ['pages', 'page-profile'],
  },
  {
    id: 'publish',
    label: 'nav.publish',
    icon: Icon.send,
    tabs: ['publish', 'studio', 'jobs', 'published'],
  },
  {
    id: 'engage',
    label: 'nav.engage',
    icon: Icon.comment,
    tabs: ['conversations', 'inbox', 'comments', 'mentions', 'utility'],
  },
  {
    id: 'activity',
    label: 'nav.activity',
    icon: Icon.logs,
    tabs: ['insights', 'logs'],
  },
  {
    id: 'settings',
    label: 'nav.settings',
    icon: Icon.user,
    /*
     * Ordered by how often somebody opens one: their own account first,
     * connections and numbers when something needs reconnecting, agents when a
     * page's behaviour changes, the platform console last and for one person.
     */
    tabs: ['account', 'connections', 'whatsapp', 'automation', 'admin'],
  },
] as const satisfies ReadonlyArray<{
  id: string;
  label: TranslationKey;
  icon: unknown;
  tabs: ReadonlyArray<TabId>;
}>;

/**
 * Selection state lives here rather than in a router: the flow is
 * source → post → targets → publish, and keeping the chosen post in one place
 * makes it obvious that Publish cannot run without a selection.
 */
export interface Selection {
  sourceKind: 'OWNED_PAGE_POST' | 'PUBLIC_PAGE_POST' | 'COMPOSITION';
  sourcePostId?: string;
  compositionId?: string;
  label: string;
  preview: string;
  /**
   * The studio project this composition was made from, and which of its files
   * are in it. Carried with the selection so that — however long the operator
   * takes to pick Pages — publishing can tell the project where its output
   * went.
   */
  projectId?: string;
  projectMediaIds?: string[];
  /** Extra Story / Reel targets the composer asked for; sent with the publish request. */
  alsoAs?: Array<'STORY' | 'REEL'>;
}

export function App() {
  const [tab, setTab] = useState<TabId>('home');
  const [selection, setSelection] = useState<Selection | null>(null);
  const [brandOpen, setBrandOpen] = useState(false);
  /**
   * A conversation the inbox asked to open, handed across as the tab changes.
   *
   * Cleared once the messages screen has taken it, so pressing that tab again
   * later returns to wherever the reader was rather than jumping back to
   * whichever row they opened last week.
   */
  const [openConversation, setOpenConversation] = useState<string | null>(null);
  /**
   * A Studio project's text and media on their way into the composer.
   *
   * Same shape as `openConversation`: set by the Studio, read once by
   * PublishFlow's handoff effect, and cleared the moment it is read so
   * returning to Publish later does not resend the same attachment.
   */
  const [studioHandoff, setStudioHandoff] = useState<StudioHandoff | null>(null);
  // Set when the Studio sends someone to the connector settings, so Automation opens on them.
  const [automationStart, setAutomationStart] = useState<'studioConnectors' | undefined>(undefined);
  useEffect(() => {
    // Only the visit that asked for it opens there; later visits start at the queue.
    if (tab !== 'automation') setAutomationStart(undefined);
  }, [tab]);
  const { t } = useI18n();

  useTableLabels();

  const me = useQuery({
    queryKey: ['me'],
    queryFn: api.me,
    retry: false,
    /*
     * Not refetched when the window regains focus.
     *
     * While signed out this endpoint answers 401 every time, so re-asking on
     * every alt-tab achieves nothing except re-rendering the shell — which was
     * enough to unmount the login form and throw away a half-finished password
     * reset. Signing in refetches explicitly, which is the only moment the
     * answer can actually have changed.
     */
    refetchOnWindowFocus: false,
  });

  /*
   * Everything the previous person cached is destroyed the moment the identity
   * changes.
   *
   * The query cache is created once and lives as long as the tab does, so
   * without this it survives signing out and signing back in as somebody else.
   * A second operator in the same browser would then be shown the first one's
   * Pages, connections, inbox and figures — real data belonging to a tenant they
   * have no relationship with — until each query happened to refetch, which
   * `staleTime` delays by design.
   *
   * Placed here, above the early returns, rather than only in the sign-in and
   * sign-out handlers: those cover the two deliberate transitions, and this
   * covers every other way an identity can change, including a session that
   * expired while the tab sat open and was replaced by a different login.
   *
   * `['me']` is spared because it is the query that just reported the change;
   * removing it here would re-enter this effect on its own refetch.
   */
  const queryClient = useQueryClient();
  const userId = me.data?.user.id ?? null;
  const lastIdentity = useRef<string | null | undefined>(undefined);

  useEffect(() => {
    if (!me.isFetched) return;

    // The first settled answer establishes the baseline; it is not a change,
    // and clearing on it would throw away a legitimately warm cache.
    if (lastIdentity.current === undefined) {
      lastIdentity.current = userId;
      return;
    }

    if (lastIdentity.current !== userId) {
      lastIdentity.current = userId;
      queryClient.removeQueries({
        predicate: (query) => query.queryKey[0] !== 'me',
      });
    }
  }, [userId, me.isFetched, queryClient]);

  const isSuperAdmin = me.data?.user.role === 'SUPERADMIN';

  // The platform console is hidden rather than shown-and-refused: an operator
  // has no use for a tab that always errors. The API enforces it regardless.
  const held = new Set(me.data?.capabilities ?? []);
  /*
   * And a second gate beside it: a channel this installation does not offer.
   *
   * The two are not interchangeable. A capability missing means "not you"; a
   * channel missing means "not here, for anybody" — the app has not been
   * approved for it, or the permission was withdrawn from a review submission
   * on purpose. Nothing is deleted when one goes: the panel, its routes and its
   * stored rows all stay, and the tab returns the moment the switch does.
   */
  const offered = new Set(me.data?.channels ?? []);
  const visibleTabs = TABS.filter((entry) => {
    if ('superAdminOnly' in entry && entry.superAdminOnly) return isSuperAdmin;
    if ('channel' in entry && !offered.has(entry.channel)) return false;
    return !('needs' in entry) || held.has(entry.needs);
  });

  /*
   * Send anybody standing in a panel they cannot open back home.
   *
   * Hiding the tab does not close every door to its panel: a shortcut on the
   * home screen, a tab still in state from before signing out, a role changed
   * while somebody was signed in. One guard here rather than one in each panel.
   */
  const onHiddenTab = me.isFetched && tab !== 'home' && !visibleTabs.some((e) => e.id === tab);
  useEffect(() => {
    if (onHiddenTab) setTab('home');
  }, [onHiddenTab]);

  // Only on the very first load. After that the decision is made from the
  // result we already have, so a background refetch never blanks the screen.
  if (!me.isFetched) return <div className="loading">{t('common.loading')}</div>;
  if (me.isError || !me.data) return <LoginPanel onLoggedIn={() => me.refetch()} />;

  const initials = (me.data?.user.displayName ?? '?').trim().charAt(0).toUpperCase();

  /*
   * The section is derived from the open panel rather than stored beside it.
   * Two sources of truth for "where am I" is how a highlighted tab ends up
   * disagreeing with the panel below it.
   */
  const section =
    SECTIONS.find((entry) => (entry.tabs as readonly string[]).includes(tab)) ?? SECTIONS[0];
  const sectionTabs = visibleTabs.filter((entry) =>
    (section.tabs as readonly string[]).includes(entry.id),
  );
  const activeTab = visibleTabs.find((entry) => entry.id === tab);

  return (
    /*
     * The active tab names itself on the wrapper, and the stylesheet turns that
     * name into a hue. Colour then carries a fact — where you are — instead of
     * decorating. Moving between sections shifts the accent, the active tab and
     * the panel's edge together, so the room changes before you read its sign.
     */
    <div className="app" data-section={tab}>
      <header className="topbar">
        {/* On a phone the full name, the account chip and the reload control do
            not fit on one line, and the header grew to three rows — a third of
            the screen spent on chrome. The name is clipped to its first word
            there and opens on a press: it is the one thing on the row a person
            already knows. */}
        <button
          type="button"
          className={brandOpen ? 'brand expanded' : 'brand'}
          onClick={() => setBrandOpen((open) => !open)}
          title={t('app.name')}
        >
          <span className="brand-mark">
            <Icon.send className="brand-icon" />
          </span>
          <span className="brand-name">{t('app.name')}</span>
        </button>

        {/* Where you are, and the way back out of it.
            A bottom bar tells you which section is lit but not which panel you
            opened inside it — and on a phone the sub-bar may have scrolled out
            of sight. The trail answers both, and its first step is a button so
            the section is one press away rather than a scroll and a guess. */}
        <div className="crumbs">
          <button
            type="button"
            className="crumb"
            onClick={() => setTab(section.tabs[0])}
            disabled={tab === section.tabs[0]}
          >
            {t(section.label)}
          </button>
          {activeTab && tab !== section.tabs[0] && (
            <>
              <Icon.chevron className="crumb-sep" />
              <span className="crumb current">{t(activeTab.label)}</span>
            </>
          )}
        </div>

        <div className="topbar-right">
          {/* Anything that is not production says so, loudly and permanently.
              Two deployments of the same dashboard look identical, and the
              only moment the difference becomes visible is after somebody has
              published from the wrong one. */}
          {me.data?.build && !me.data.build.isProduction && (
            <span className="env-badge" title={me.data.build.commit ?? undefined}>
              {me.data.build.environment}
            </span>
          )}

          {/* Reloading is not a preference, so it stays out in the open —
              a phone browser offers no Ctrl+F5 and pull-to-refresh does not
              exist inside an app shell that never scrolls its own document. */}
          <button
            type="button"
            className="ghost icon-only"
            onClick={() => window.location.reload()}
            title={t('app.refresh')}
            aria-label={t('app.refresh')}
          >
            <Icon.refresh />
          </button>

          {/* Opens to say which account this window is signed into. Signing
              out moved inside it: the two belong together, and a lone icon
              beside the name was easy to press by accident. */}
          <AccountMenu
            displayName={me.data?.user.displayName ?? ''}
            email={me.data?.user.email ?? ''}
            role={me.data?.user.role ?? ''}
            initials={initials}
            build={
              me.data?.build ?? {
                environment: 'production',
                isProduction: true,
                commit: null,
                builtAt: null,
              }
            }
            onSignOut={async () => {
              await api.logout();
              // Immediately, rather than waiting for the identity effect: the
              // next screen is the login form, and nothing belonging to this
              // operator should still be in memory behind it.
              queryClient.clear();
              await me.refetch();
            }}
          />
        </div>
      </header>

      {/* Five destinations. On a phone the stylesheet moves this to the bottom
          of the viewport, where a thumb reaches without stretching. */}
      <nav className="sections">
        {SECTIONS.filter((entry) =>
          visibleTabs.some((leaf) => (entry.tabs as readonly string[]).includes(leaf.id)),
        ).map((entry) => {
          const SectionIcon = entry.icon as (p: { className?: string }) => ReactElement;
          const active = entry.id === section.id;
          return (
            <button
              key={entry.id}
              type="button"
              className={active ? 'section-tab active' : 'section-tab'}
              aria-current={active ? 'page' : undefined}
              onClick={() => {
                /*
                 * Land on the first panel of the section, unless already inside
                 * it — and on the first one *this person can open*. Sections do
                 * not all begin with the panel everybody holds: "Pages" starts
                 * at Connections, which is an administrator's screen, so an
                 * operator pressing the section landed on a panel that was
                 * hidden from their own tab row.
                 */
                if (active) return;
                const first = visibleTabs.find((leaf) =>
                  (entry.tabs as readonly string[]).includes(leaf.id),
                );
                if (first) setTab(first.id);
              }}
            >
              <SectionIcon />
              <span>{t(entry.label)}</span>
            </button>
          );
        })}
      </nav>

      {/* Shown only where the section actually holds more than one panel, so a
          single-panel section does not carry an empty row of chrome. */}
      {sectionTabs.length > 1 && (
        <nav className="subtabs">
          {sectionTabs.map((entry) => (
            <button
              key={entry.id}
              type="button"
              className={tab === entry.id ? 'subtab active' : 'subtab'}
              onClick={() => setTab(entry.id)}
            >
              {t(entry.label)}
            </button>
          ))}
        </nav>
      )}

      {selection && (
        <div className="selection-bar">
          <Icon.check />
          <strong>{t('selection.label')}:</strong> {selection.label}
          <span className="preview">{selection.preview}</span>
          <button type="button" className="ghost small" onClick={() => setSelection(null)}>
            {t('selection.clear')}
          </button>
        </div>
      )}

      <main>
        {tab === 'home' && (
          <HomePanel
            onGo={(next) => setTab(next as TabId)}
            canOpen={(id) => visibleTabs.some((entry) => entry.id === id)}
          />
        )}
        {tab === 'connections' && (
          <>
            <ConnectionsPanel />
            <SocialConnectionsPanel />
          </>
        )}
        {tab === 'pages' && <PagesPanel />}
        {tab === 'page-profile' && <PageProfilePanel />}
        {tab === 'whatsapp' && <WhatsappPanel />}
        {tab === 'publish' && (
          <PublishFlow
            selection={selection}
            onSelect={setSelection}
            onClear={() => setSelection(null)}
            handoff={studioHandoff}
            onHandoffConsumed={() => setStudioHandoff(null)}
          />
        )}
        {tab === 'studio' && (
          <StudioPanel
            onSendToPublish={(handoff) => {
              setStudioHandoff(handoff);
              setTab('publish');
            }}
            onOpenScheduled={() => setTab('jobs')}
            onOpenConnectors={() => {
              setAutomationStart('studioConnectors');
              setTab('automation');
            }}
          />
        )}
        {tab === 'conversations' && (
          <ConversationsPanel
            openId={openConversation}
            onOpened={() => setOpenConversation(null)}
          />
        )}
        {tab === 'inbox' && (
          <InboxPanel
            onOpenConversation={(id) => {
              setOpenConversation(id);
              setTab('conversations');
            }}
          />
        )}
        {tab === 'comments' && <CommentsPanel />}
        {tab === 'mentions' && <MentionsPanel />}
        {tab === 'utility' && <UtilityTemplatesPanel />}
        {tab === 'automation' && <AutomationPanel initialTab={automationStart} />}
        {tab === 'jobs' && <JobsPanel />}
        {tab === 'published' && <PublishedPostsPanel />}
        {tab === 'insights' && <InsightsPanel />}
        {tab === 'logs' && <LogsPanel />}
        {tab === 'account' && <AccountPanel />}
        {tab === 'admin' && isSuperAdmin && <AdminPanel />}
      </main>
    </div>
  );
}

export function LanguageButton() {
  const { t, toggleLanguage } = useI18n();
  return (
    <button type="button" className="ghost small" onClick={toggleLanguage}>
      <Icon.globe />
      {t('app.language')}
    </button>
  );
}

export function ThemeButton() {
  const { theme, toggleTheme } = useTheme();
  const { t } = useI18n();
  const label = theme === 'dark' ? t('app.theme.toLight') : t('app.theme.toDark');

  return (
    <button
      type="button"
      className="ghost icon-only"
      onClick={toggleTheme}
      title={label}
      aria-label={label}
    >
      {theme === 'dark' ? <Icon.sun /> : <Icon.moon />}
    </button>
  );
}
